import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { LogicalOperatorType, OperatorType } from '../models/FilterModel';
import { FilterKey } from '../types/FilterKey';
import { FunctionComponent, useState } from 'react';

interface StringComparisonOperatorSelectProps {
  label: string;
  filterKey: FilterKey;
  defaultOperatorValue?: OperatorType;
  defaultLogicalOperatorValue?: LogicalOperatorType;
  handleChange: (newSelection: StringComparisonOperatorSelectOption) => void;
}

export interface StringComparisonOperatorSelectOption {
  value: number;
  operator: OperatorType;
  logicalOperator: LogicalOperatorType;
  label: string;
}

export const StringComparisonOperatorSelect: FunctionComponent<StringComparisonOperatorSelectProps> = ({
  label,
  filterKey,
  defaultOperatorValue,
  defaultLogicalOperatorValue,
  handleChange,
}) => {
  let OPERATOR_OPTIONS: StringComparisonOperatorSelectOption[] = [
    {
      value: 1,
      label: 'OR',
      operator: OperatorType.LIKE,
      logicalOperator: LogicalOperatorType.OR,
    },
    {
      value: 2,
      label: 'AND',
      operator: OperatorType.LIKE,
      logicalOperator: LogicalOperatorType.AND,
    },
  ];

  if (filterKey == FilterKey.CAMPAIGN_NAME_NOT) {
    // For example, we have following campaigns
    // 1)  "christmas ice cubes special"
    // 2)  "christmas hats special"
    // 3)  "christmas calendars"
    // 4)  "ice cubes special"
    // 5)  "fast laptops"
    // So, searching with terms "christmas" and "special":
    // Doesn't contain (OR) will return only campaign "fast laptops" (search for campaigns whose name does not contain "christmas" nor "special")
    // Doesn't contain (AND) will return  "christmas calendars", "ice cubes special", "fast laptops"

    OPERATOR_OPTIONS = [
      {
        value: 3,
        label: 'OR',
        operator: OperatorType.NOT_LIKE,
        logicalOperator: LogicalOperatorType.AND, // we need to combine  NOT LIKE with AND to get expected result
      }, // search for campaigns whose name does not contain "word_x" nor "word_y". If campaign name contains "word_x" or "word_y" or both then it will NOT be shown
      {
        value: 4,
        label: 'AND',
        operator: OperatorType.NOT_LIKE,
        logicalOperator: LogicalOperatorType.OR, // we need to combine NOT LIKE with OR to get expected result
      },
    ];
  }

  const defaultSelectedOption = OPERATOR_OPTIONS.find(
    (option) => option.operator === defaultOperatorValue && option.logicalOperator === defaultLogicalOperatorValue,
  );
  const [selectedOption, setSelectedOption] = useState<StringComparisonOperatorSelectOption>(defaultSelectedOption ?? OPERATOR_OPTIONS[0]);

  function onSelectionChanged(event: SelectChangeEvent<number>) {
    if (!event.target.value) {
      return;
    }
    const newSelection = OPERATOR_OPTIONS.find((option) => option.value === event.target.value);
    if (!newSelection) {
      return;
    }
    setSelectedOption(newSelection);
    handleChange(newSelection);
  }

  return (
    <FormControl className={'w-52'}>
      <InputLabel id={'text-operator-label-' + filterKey}>{label}</InputLabel>
      <Select
        labelId={'string-comparison-operator-label-' + filterKey}
        label={label}
        id={'string-comparison-operator-' + filterKey}
        value={selectedOption.value}
        onChange={onSelectionChanged}
      >
        {OPERATOR_OPTIONS.map((option) => {
          return (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
